import { FC, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { DataGrid } from "../../../../components/datagrid";
import { pruneFilterModel, useGridView } from "../../../../atoms/gridViewAtoms";
import { useCountryColumns } from "./hooks/useCountryColumns";
import { useNavigate } from "react-router-dom";
import { useCountryOverview } from "../../dataHooks/countryQueries";
import { useRoles } from "../../../../auth/useRoles";
import {
  hasAnyAppRole,
  isGlobalLifecycleRole
} from "../../../../auth/permissions";

type Props = {};

export const CountriesGrid: FC<Props> = () => {
  const { data, isLoading, isFetching } = useCountryOverview();

  const rows = data || [];
  const navigate = useNavigate();
  const roles = useRoles();
  const columns = useCountryColumns(rows, isGlobalLifecycleRole(roles));
  const [view, setView] = useGridView("countries");

  // The MA Handler column only exists for global lifecycle roles, so a view saved by an admin
  // can carry a filter on a field this user has no column for.
  const filterModel = useMemo(
    () =>
      pruneFilterModel(view.filterModel, new Set(columns.map((c) => c.field))),
    [view.filterModel, columns]
  );

  const showGrantModeEmptyState =
    !isLoading && !isFetching && rows.length === 0 && !hasAnyAppRole(roles);

  if (showGrantModeEmptyState) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          textAlign: "center"
        }}
      >
        <Typography variant="h6">
          You do not have access to any country additions yet.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={() => navigate("/countries/request-new")}
          >
            Request a country addition
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/countries/my-requests")}
          >
            My requests
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <DataGrid
      loading={isLoading || isFetching}
      columns={columns}
      rows={rows}
      getRowId={(row) => row.id}
      onRowClick={(r) => navigate(`country/${r.id}`)}
      searchPlaceholder="Search country additions"
      exportFileName="Country additions"
      filterModel={filterModel}
      onFilterModelChange={(next) =>
        setView((prev) => ({ ...prev, filterModel: next }))
      }
      sortModel={view.sortModel}
      onSortModelChange={(next) =>
        setView((prev) => ({ ...prev, sortModel: next }))
      }
      sx={{
        "&.MuiDataGrid-root .MuiDataGrid-cell:focus": {
          outline: "none"
        }
      }}
    />
  );
};
